// CustomerSearchSelectAsync.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";

const RPP = 25;

const isArchivedCustomer = (customer) =>
  String(customer?.status || "").trim().toLowerCase() === "archived";

export const isCreditHoldCustomer = (customer) => {
  if (!customer) return false;
  if (customer.isCreditHold === true) return true;
  if (customer.termsIsCreditHold === true) return true;
  // Temporary legacy fallback while old literal term data is migrated to Terms.isCreditHold.
  const termsName = String(customer.termsName || customer.termName || "").trim().toLowerCase();
  const termsRaw = String(customer.terms || "").trim().toLowerCase();
  return termsName === "credit hold" || termsRaw === "credit hold";
};

export default function CustomerSearchSelectAsync({
  instanceUniqueId,
  value,                 // number | "" (customerId)
  onChange,              // (id|null) => void
  onSelectCustomer,      // (customerObj|null) => void  ← NEW (optional)
  label = "Customer Account",
  showLabel = true,
  inputId,
  error = false,
  helperText = "",
  disabled = false,
  disableClearable = false,
  borderlessInput = false,
}) {
  const [open, setOpen]               = useState(false);
  const [inputValue, setInputValue]   = useState("");
  const [options, setOptions]         = useState([]);  // array of customers
  const [page, setPage]               = useState(1);
  const [hasNext, setHasNext]         = useState(false);
  const [loading, setLoading]         = useState(false);
  const [selected, setSelected]       = useState(null); // the selected option object
  const [blockedMessage, setBlockedMessage] = useState("");
  const reqIdRef = useRef(0);

  // Keep controlled value in sync with passed id
  useEffect(() => {
    const normalizedValue = value ? String(value) : "";
    const selectedId = selected?.id != null ? String(selected.id) : "";

    if (!normalizedValue) {
      setSelected(null);
      return;
    }

    if (selectedId && selectedId === normalizedValue) return;

    const existing = options.find((opt) => String(opt.id) === normalizedValue);
    if (existing) {
      setSelected(existing);
      return;
    }

    let ignore = false;

    async function fetchSelected() {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/customers/${value}`,
          {
            headers: { accessToken: localStorage.getItem("accessToken") },
          }
        );

        if (ignore) return;

        const customer = data?.customer || data;
        if (customer && customer.id && !isArchivedCustomer(customer)) {
          setOptions((prev) => {
            const exists = prev.some((opt) => opt.id === customer.id);
            return exists ? prev : [customer, ...prev];
          });
          setSelected(customer);
          if (onSelectCustomer) onSelectCustomer(customer);
        } else if (isArchivedCustomer(customer)) {
          setSelected(null);
          setOptions((prev) => prev.filter((opt) => String(opt?.id) !== String(customer?.id)));
          onChange(null);
          if (onSelectCustomer) onSelectCustomer(null);
        }
      } catch (e) {
        console.error("Failed to load customer", e);
      }
    }

    fetchSelected();

    return () => {
      ignore = true;
    };
  }, [value, selected, options]);

  const fetchPage = useCallback(async ({ q, pageNum, append }) => {
    const myReqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/customers/get-customers`,
        {
          params: {
            q: (q || "").trim(),
            page: pageNum,
            rpp: RPP,
            tab: "all",
            instanceUniqueId,
          },
          headers: { accessToken: localStorage.getItem("accessToken") },
        }
      );
      // ignore stale responses
      if (myReqId !== reqIdRef.current) return;

      const rows = (data.customers || data.Results || [])
        .filter((customer) => !isArchivedCustomer(customer));
      setOptions(prev => append ? [...prev, ...rows] : rows);
      setHasNext(Boolean(data.hasNext));
      setPage(data.page || pageNum);
    } catch (e) {
      if (myReqId !== reqIdRef.current) return;
      console.error("Customer search failed:", e);
      if (!append) setOptions([]);
      setHasNext(false);
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [instanceUniqueId]);

  // Single debounced effect: runs when opened OR input changes
  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(() => {
      fetchPage({ q: inputValue, pageNum: 1, append: false });
    }, 350);
    return () => clearTimeout(handle);
  }, [open, inputValue, fetchPage]);

  // Keep controlled value in sync if parent passes an id (e.g., Formik reinit)
  // If you want: you can fetch that one id here and setSelected; leaving out to avoid extra calls.

  const handleChange = (_, opt) => {
    if (isArchivedCustomer(opt)) {
      setBlockedMessage("Archived customers cannot be selected for a new project.");
      return;
    }
    if (isCreditHoldCustomer(opt)) {
      setBlockedMessage("This customer is on Credit Hold and cannot be selected.");
      return;
    }

    setBlockedMessage("");
    setSelected(opt || null);
    onChange(opt ? opt.id : null);
    if (onSelectCustomer) onSelectCustomer(opt || null);
  };

  const listboxProps = {
    onScroll: (e) => {
      const el = e.currentTarget;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
      if (nearBottom && hasNext && !loading) {
        fetchPage({ q: inputValue, pageNum: page + 1, append: true });
      }
    },
    style: { maxHeight: 360, overflow: "auto" },
  };

  const getOptionLabel = (c) => {
    if (!c) return "";
    const name = c.businessName || `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || "Unnamed";
    return name;
  };

  const valueObj = useMemo(() => (selected ? selected : null), [selected]);

  const selectedCustomerUrl = instanceUniqueId && selected?.id
    ? `/instance/${instanceUniqueId}/sales/customers?tab=all&openCustomer=${selected.id}`
    : "";

  return (
    <FormControl fullWidth error={!!error} className="search-by-customer-container">
      <Box
        sx={{
          position: "relative",
        }}
      >
        <Autocomplete
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          options={options}
          value={valueObj}
          onChange={handleChange}
          disabled={disabled}
          disableClearable={disableClearable}
          // MUI otherwise hides this action until hover. Keep it visible when
          // the caller explicitly permits clearing the selected customer.
          componentsProps={selected && !disabled && !disableClearable
            ? { clearIndicator: { style: { visibility: "visible" } } }
            : undefined}
          getOptionDisabled={(option) => isCreditHoldCustomer(option) || isArchivedCustomer(option)}
          onInputChange={(_, val, reason) => {
            if (reason === "input") setInputValue(val);
          }}
          loading={loading}
          ListboxProps={listboxProps}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              id={inputId}
              label={showLabel ? label : undefined}
              size="small"
              disabled={disabled}
              inputProps={{
                ...params.inputProps,
                ...(borderlessInput
                  ? { style: { ...params.inputProps?.style, border: 0 } }
                  : {}),
              }}
              InputProps={{
                ...params.InputProps,
                readOnly: disabled,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              placeholder="Search by name, email, or phone…"
            />
          )}
          renderOption={(props, c) => {
            const name = getOptionLabel(c);
            const email = (c.emailAddress || "").trim();
            const phone = c.phoneNumber ? ` • ${c.phoneNumber}` : "";
            const creditHold = isCreditHoldCustomer(c);
            let logoUrl = "";
            if (c.customUploadedBrandLogo) {
              logoUrl = c.customUploadedBrandLogo.startsWith("uploads")
                ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/${c.customUploadedBrandLogo}`
                : c.customUploadedBrandLogo;
            }
            const optionProps = creditHold
              ? {
                  ...props,
                  onMouseDown: (event) => {
                    setBlockedMessage("This customer is on Credit Hold and cannot be selected.");
                    props.onMouseDown?.(event);
                  },
                  onClick: (event) => {
                    setBlockedMessage("This customer is on Credit Hold and cannot be selected.");
                    props.onClick?.(event);
                  },
                }
              : props;
            return (
              <li {...optionProps} key={c.id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, opacity: creditHold ? 0.65 : 1, width: "100%" }}>
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt=""
                      width={24}
                      height={24}
                      style={{ objectFit: "contain" }}
                    />
                  ) : (
                    <Box sx={{ width: 24, height: 24 }} />
                  )}
                  <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography variant="body2">{name}</Typography>
                      {creditHold ? (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label="Credit Hold — cannot be selected"
                        />
                      ) : null}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {email}{phone}
                    </Typography>
                  </Box>
                </Box>
              </li>
            );
          }}
        />
        {selectedCustomerUrl ? (
          <Box
            component="a"
            className="customer-select-view"
            href={selectedCustomerUrl}
            target="_blank"
            rel="noopener noreferrer"
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={(event) => event.stopPropagation()}
            sx={{
              position: "absolute",
              top: "50%",
              right: 72,
              transform: "translateY(-50%)",
              fontSize: 12,
              fontWeight: 600,
              color: "primary.main",
              textDecoration: "none",
              opacity: 1,
              pointerEvents: "auto",
              padding: "2px 6px",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            View
          </Box>
        ) : null}
      </Box>
      {blockedMessage ? <FormHelperText error>{blockedMessage}</FormHelperText> : null}
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  );
}
